import React, { useEffect, useRef, useState } from "react";
import adminService from "../../../services/adminService";

// Admin "Home Page" tab: add and remove the social media posts shown in the
// homepage's "Follow JUCO Pipeline" section. The newest post takes the large
// featured spot and the next four fill the "Recent" column.

const PLATFORMS = [
  { value: "instagram", label: "Instagram", example: "https://www.instagram.com/p/..." },
  { value: "x", label: "X", example: "https://x.com/JucoPipeline/status/..." },
];

const MAX_THUMBNAIL_MB = 5;
const EMPTY_FORM = { platform: "instagram", url: "", headline: "", thumbnail: null };

const inputClass =
  "h-11 w-full rounded-lg px-3 bg-jp-field border border-jp-line text-sm text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:border-jp-accent focus:ring-1 focus:ring-jp-accent transition-all";

const formatDate = (date) =>
  new Date(date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const errorMessage = (error, fallback) => error?.response?.data?.message || fallback;

export const HomePagePostsPage = () => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const fileInputRef = useRef(null);

  const loadPosts = async () => {
    setListError("");
    try {
      const data = await adminService.getSocialPosts();
      setPosts(data.posts || []);
    } catch (error) {
      setListError(errorMessage(error, "Could not load posts."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  // Free the thumbnail preview when it changes or the page closes
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormError("");
    setSuccess("");
  };

  const handleThumbnail = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setFormError("The thumbnail must be an image (JPG, PNG, GIF or WebP).");
      return;
    }
    if (file.size > MAX_THUMBNAIL_MB * 1024 * 1024) {
      setFormError(`The thumbnail must be under ${MAX_THUMBNAIL_MB} MB.`);
      return;
    }
    handleChange("thumbnail", file);
    setPreview(URL.createObjectURL(file));
  };

  const clearThumbnail = () => {
    handleChange("thumbnail", null);
    setPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.url.trim()) return setFormError("Add the link to the post.");
    if (!form.headline.trim()) return setFormError("Add a headline.");
    if (!form.thumbnail) return setFormError("Add a thumbnail image.");

    const formData = new FormData();
    formData.append("platform", form.platform);
    formData.append("url", form.url.trim());
    formData.append("headline", form.headline.trim());
    formData.append("thumbnail", form.thumbnail);

    setSaving(true);
    try {
      await adminService.createSocialPost(formData);
      setForm(EMPTY_FORM);
      clearThumbnail();
      setSuccess("Post added. It's now the featured post on the home page.");
      loadPosts();
    } catch (error) {
      setFormError(errorMessage(error, "Could not add the post. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Remove "${post.headline}" from the home page?`)) return;
    setDeletingId(post.id);
    try {
      await adminService.deleteSocialPost(post.id);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
    } catch (error) {
      setListError(errorMessage(error, "Could not remove the post."));
    } finally {
      setDeletingId(null);
    }
  };

  const selectedPlatform = PLATFORMS.find((p) => p.value === form.platform);

  return (
    <div className="space-y-6 text-left">
      {/* Add a post */}
      <form onSubmit={handleSubmit} className="rounded-3xl bg-jp-card border border-jp-line shadow-sm p-6 space-y-5">
        <div>
          <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest">Add a Social Media Post</h2>
          <p className="text-jp-muted text-xs mt-1">
            Shows in the "Follow JUCO Pipeline" section of the home page. The newest post is featured.
          </p>
        </div>

        {/* Platform */}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-jp-ink">Platform</span>
          <div className="inline-flex w-fit rounded-lg border border-jp-line bg-jp-section p-1" role="radiogroup" aria-label="Platform">
            {PLATFORMS.map((platform) => (
              <button
                key={platform.value}
                type="button"
                role="radio"
                aria-checked={form.platform === platform.value}
                onClick={() => handleChange("platform", platform.value)}
                className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
                  form.platform === platform.value
                    ? "bg-jp-accent/15 border border-jp-accent/50 text-jp-accent-ink"
                    : "border border-transparent text-jp-muted hover:text-jp-ink"
                }`}
              >
                {platform.label}
              </button>
            ))}
          </div>
        </div>

        {/* Link */}
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-jp-ink">Link to the post</span>
          <input
            type="url"
            className={inputClass}
            placeholder={selectedPlatform.example}
            value={form.url}
            onChange={(e) => handleChange("url", e.target.value)}
          />
        </label>

        {/* Headline */}
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-jp-ink">Headline</span>
          <input
            type="text"
            className={inputClass}
            placeholder="e.g. Fall Ball Report: Gulf Coast State College"
            maxLength={200}
            value={form.headline}
            onChange={(e) => handleChange("headline", e.target.value)}
          />
        </label>

        {/* Thumbnail */}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-jp-ink">Thumbnail</span>
          {preview ? (
            <div className="flex items-center gap-4">
              <img src={preview} alt="Thumbnail preview" className="w-40 h-24 rounded-xl object-cover border border-jp-line" />
              <button type="button" onClick={clearThumbnail} className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline">
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full sm:w-80 h-24 rounded-xl border-2 border-dashed border-jp-line text-jp-muted hover:border-jp-accent hover:text-jp-accent text-sm font-medium transition-colors"
            >
              Choose an image
              <span className="block text-[11px] font-normal mt-1">JPG, PNG, GIF or WebP, up to {MAX_THUMBNAIL_MB} MB</span>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={(e) => handleThumbnail(e.target.files?.[0])}
          />
        </div>

        {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}
        {success && <p className="text-sm text-green-600 dark:text-green-400">{success}</p>}

        <button
          type="submit"
          disabled={saving}
          className="min-w-[160px] h-10 px-5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? "Adding..." : "Add Post"}
        </button>
      </form>

      {/* Current posts */}
      <div className="rounded-3xl bg-jp-card border border-jp-line shadow-sm p-6">
        <h2 className="text-jp-accent-ink text-sm font-bold uppercase tracking-widest mb-4">Posts on the Home Page</h2>

        {listError && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{listError}</p>}

        {loading ? (
          <p className="text-sm text-jp-muted">Loading...</p>
        ) : posts.length === 0 ? (
          <p className="text-sm text-jp-muted">No posts added here yet. The home page is showing its built-in posts.</p>
        ) : (
          <div className="divide-y divide-jp-line">
            {posts.map((post, index) => (
              <div key={post.id} className="flex items-center gap-4 py-3">
                <img src={post.image} alt="" className="w-24 h-16 flex-shrink-0 rounded-lg object-cover bg-jp-section border border-jp-line" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-jp-ink truncate">{post.headline}</p>
                  <p className="text-xs text-jp-muted flex flex-wrap items-center gap-x-2">
                    <span>{post.platform === "x" ? "X" : "Instagram"}</span>
                    <span>·</span>
                    <span>{formatDate(post.date)}</span>
                    {index === 0 && <span className="text-jp-accent-ink font-semibold">· Featured</span>}
                    {index > 4 && <span>· Not shown (only the newest 5 appear)</span>}
                  </p>
                  <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-xs text-jp-accent-ink hover:underline truncate block">
                    {post.url}
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(post)}
                  disabled={deletingId === post.id}
                  className="px-3 py-1.5 rounded-full border border-red-500/50 text-red-600 dark:text-red-400 hover:bg-red-500/10 text-xs font-medium transition-colors disabled:opacity-50"
                >
                  {deletingId === post.id ? "Removing..." : "Remove"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePagePostsPage;
